"use client"

import { useRouter } from "next/navigation"
import { useTranslations } from "@/lib/intl/use-translations"
import { useQuizProgress } from "@/components/quiz/progress-context"

const ICON_BUTTON_CLASSES =
  "inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-900 md:h-10 md:w-10"

const BACK_ARROW_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
)

/**
 * Chrome back affordance. Always shows the circular ← icon button:
 *  - Step 1 (no wizard back available): exits the quiz to home.
 *  - Step 2+ (wizard.back registered via context): steps the wizard
 *    back one question, and a separate plain-text "Back" label
 *    renders to the right as a second clickable target.
 */
export function QuizBackButton({ homeHref }: { homeHref: string }) {
  const router = useRouter()
  const t = useTranslations("quiz")
  const ctx = useQuizProgress()
  const stepBack = ctx?.back
  const handleClick = () => {
    if (stepBack) stepBack()
    else router.push(homeHref)
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        aria-label={t("backLabel")}
        className={ICON_BUTTON_CLASSES}
      >
        {BACK_ARROW_SVG}
      </button>
      {stepBack && (
        <button
          type="button"
          onClick={handleClick}
          className="type-body-lg cursor-pointer text-neutral-700 transition-colors hover:text-neutral-900"
        >
          {t("backLabel")}
        </button>
      )}
    </div>
  )
}
